import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../../../utils/requestMethods";
import { Header, SkeletonTable, AlertNotification, BackButton } from "../../../components";
import { useStateContext } from "../../../contexts/ContextProvider";
import { MdSearch, MdWbSunny, MdCloud } from "react-icons/md";
import * as Icons from "../../../data/teacher";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
};

const StudentAttendance = ({ history }) => {
  const { school } = useStateContext();
  const location = useLocation();
  const classId = location.pathname.split('/')[4];
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;

  const [filterText, setFilterText] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [studentAttendance, setStudentAttendance] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [period, setPeriod] = useState("morning");
  const [loading, setLoading] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedForUnmark, setSelectedForUnmark] = useState(new Set());
  const [showUnmarkModal, setShowUnmarkModal] = useState(false);
  const [unmarking, setUnmarking] = useState(false);

  const [showAbsentReasonModal, setShowAbsentReasonModal] = useState(false);
  const [absentReasons, setAbsentReasons] = useState({});
  const [sharedReason, setSharedReason] = useState('');

  useEffect(() => {
    if (classId && schoolId) {
      fetchStudents(classId).then((fetched) => {
        if (fetched?.length > 0) {
          fetchAttendanceForPeriod(fetched, date, period);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (classId && schoolId && students.length > 0) {
      setSelectedForUnmark(new Set());
      fetchAttendanceForPeriod(students, date, period);
    }
  }, [date, period]);

  const fetchStudents = async (id) => {
    if (!id) return [];
    try {
      setLoading(true);
      const { data } = await userRequest.get(`children/class/${id}?limit=200`);
      setStudents(data.children);
      return data.children;
    } catch (err) {
      console.error("Error fetching students:", err);
      setError("Error fetching students");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceForPeriod = async (studentsList, selectedDate, selectedPeriod) => {
    if (!classId || !schoolId || !selectedDate) return;
    setAttendanceLoading(true);
    try {
      const { data } = await userRequest.get(
        `/attendance/class/${classId}?school_id=${schoolId}&date=${selectedDate}&term=${schoolTerm}&session=${schoolSession}&from=${selectedDate}&to=${selectedDate}&limit=50`
      );

      // Records without a period field are treated as morning (backward compatibility).
      // Only count students who have a record for the selected period (POST vs PUT decision)
      const periodStudents = (data?.students || []).filter((item) =>
        item.attendance?.[0]?.records?.some((r) => (r.period || 'morning') === selectedPeriod)
      );
      setStudentAttendance(periodStudents);

      if (data?.students?.length > 0) {
        const updatedAttendance = {};
        data.students.forEach((item) => {
          const records = item.attendance?.[0]?.records;
          const periodRecord = records?.find((r) => (r.period || 'morning') === selectedPeriod);
          updatedAttendance[item.student.id] = periodRecord ? periodRecord.status : null;
        });
        setAttendance(updatedAttendance);
      } else {
        initializeAttendance(studentsList);
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setError("Error fetching attendance");
      initializeAttendance(studentsList);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const initializeAttendance = (studentsList) => {
    if (!studentsList?.length) return;
    const init = {};
    studentsList.forEach((s) => { init[s._id] = null; });
    setAttendance(init);
  };

  const handleAttendanceChange = (childId, status) => {
    setAttendance((prev) => ({ ...prev, [childId]: status }));
  };

  const handleSaveClick = () => {
    const absentStudents = students.filter((s) => attendance[s._id] === 2);
    if (absentStudents.length > 0) {
      const reasons = {};
      absentStudents.forEach((s) => { reasons[s._id] = ''; });
      setAbsentReasons(reasons);
      setSharedReason('');
      setShowAbsentReasonModal(true);
    } else {
      saveAttendance({});
    }
  };

  const saveAttendance = async (reasons) => {
    setSaving(true);
    setShowAbsentReasonModal(false);
    const defaultReason = period === "morning" ? "Morning attendance" : "Afternoon attendance";
    const attendanceBase = Object.entries(attendance).map(([childId, status]) => {
      const entry = { childId, status: status === null ? 0 : status };
      if (status === 1) entry.reason = defaultReason;
      if (status === 2) entry.reason = reasons[childId]?.trim() || 'Absent';
      return entry;
    });
    const label = period === "morning" ? "Morning" : "Afternoon";

    try {
      let responseData;
      if (studentAttendance.length > 0) {
        const { data } = await userRequest.put("/attendance/edit-multiple-student-attendance", {
          attendance: attendanceBase.map((item) => ({ ...item, unmark: false })),
          classId, schoolId, date, term: schoolTerm, session: schoolSession, period,
        });
        responseData = data;
      } else {
        const { data } = await userRequest.post("/attendance/mark-multiple-student-attendance", {
          attendance: attendanceBase,
          classId, schoolId, date, period,
        });
        responseData = data;
      }

      const errors = responseData?.errors || [];
      const saved = responseData?.results || [];

      if (errors.length > 0) {
        const uniqueMessages = [...new Set(errors.map((e) => e.error))];
        const msg = uniqueMessages.join('. ');
        if (saved.length > 0) {
          setSuccess(`${saved.length} saved. ${errors.length} failed: ${msg}.`);
          setTimeout(() => setSuccess(""), 7000);
        } else {
          setError(`Could not save attendance: ${msg}.`);
          setTimeout(() => setError(""), 7000);
        }
      } else {
        setSuccess(`${label} attendance ${studentAttendance.length > 0 ? 'updated' : 'saved'} successfully!`);
        setTimeout(() => setSuccess(""), 5000);
      }
      // Refresh so studentAttendance reflects saved records — next save will correctly use PUT
      await fetchAttendanceForPeriod(students, date, period);
    } catch (err) {
      console.error("Error saving attendance:", err);
      setError("Error saving attendance");
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const filteredData = students
    ?.filter((item) =>
      item.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.lastname?.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) => (a.lastname || '').localeCompare(b.lastname || ''));

  const markAll = (status) => {
    const updated = { ...attendance };
    filteredData.forEach((s) => { updated[s._id] = status; });
    setAttendance(updated);
  };

  const totalStudents = students.length;
  const presentCount = students.filter((s) => attendance[s._id] === 1).length;
  const absentCount = students.filter((s) => attendance[s._id] === 2).length;
  const pendingCount = students.filter((s) => attendance[s._id] !== 1 && attendance[s._id] !== 2).length;

  const markedStudents = filteredData?.filter((s) => attendance[s._id] === 1 || attendance[s._id] === 2) ?? [];
  const allMarkedSelected = markedStudents.length > 0 && markedStudents.every((s) => selectedForUnmark.has(s._id));

  const toggleSelectForUnmark = (id) => {
    setSelectedForUnmark((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allMarkedSelected) {
      setSelectedForUnmark(new Set());
    } else {
      setSelectedForUnmark(new Set(markedStudents.map((s) => s._id)));
    }
  };

  const openUnmarkModal = (singleId = null) => {
    if (singleId) setSelectedForUnmark(new Set([singleId]));
    setShowUnmarkModal(true);
  };

  const confirmUnmark = async () => {
    setUnmarking(true);
    try {
      await userRequest.put("/attendance/edit-multiple-student-attendance", {
        attendance: [...selectedForUnmark].map((childId) => ({
          childId,
          status: attendance[childId] ?? 0,
          unmark: true,
        })),
        classId, schoolId, date,
        term: schoolTerm,
        session: schoolSession,
        period,
      });
      const count = selectedForUnmark.size;
      setSuccess(`Attendance unmarked for ${count} student${count > 1 ? "s" : ""}.`);
      setSelectedForUnmark(new Set());
      setShowUnmarkModal(false);
      fetchAttendanceForPeriod(students, date, period);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error unmarking attendance:", err);
      setError("Error unmarking attendance");
      setTimeout(() => setError(""), 5000);
    } finally {
      setUnmarking(false);
    }
  };

  const columns = [
    {
      name: (
        <input
          type="checkbox"
          checked={allMarkedSelected}
          onChange={toggleSelectAll}
          disabled={markedStudents.length === 0}
          className="w-4 h-4 rounded cursor-pointer accent-indigo-600"
          title="Select all marked students"
        />
      ),
      cell: (row) => {
        const isMarked = attendance[row._id] === 1 || attendance[row._id] === 2;
        if (!isMarked) return <span className="w-4 h-4 block" />;
        return (
          <input
            type="checkbox"
            checked={selectedForUnmark.has(row._id)}
            onChange={() => toggleSelectForUnmark(row._id)}
            className="w-4 h-4 rounded cursor-pointer accent-indigo-600"
          />
        );
      },
      width: "52px",
      ignoreRowClick: true,
    },
    {
      name: "Student Name",
      cell: (row) => (
        <span className="font-medium">{row.lastname} {row.firstname}</span>
      ),
    },
    {
      name: "Present",
      cell: (row) => (
        <div className="flex items-center space-x-4">
          <input
            type="radio"
            name={`attendance-${row._id}`}
            id={`present-${row._id}`}
            className="hidden peer"
            checked={attendance[row._id] === 1}
            onChange={() => handleAttendanceChange(row._id, 1)}
          />
          <label htmlFor={`present-${row._id}`} className="cursor-pointer p-1 bg-gray-300 rounded-full">
            <img
              src={attendance[row._id] === 1 ? Icons.studentPresent : Icons.studentPending}
              alt="Present Icon"
            />
          </label>
        </div>
      ),
    },
    {
      name: "Absent",
      cell: (row) => (
        <div className="flex items-center space-x-4">
          <input
            type="radio"
            name={`attendance-${row._id}`}
            id={`absent-${row._id}`}
            className="hidden peer"
            checked={attendance[row._id] === 2}
            onChange={() => handleAttendanceChange(row._id, 2)}
          />
          <label htmlFor={`absent-${row._id}`} className="cursor-pointer p-1 bg-gray-300 rounded-full">
            <img
              src={attendance[row._id] === 2 ? Icons.studentAbsent : Icons.studentPending}
              alt="Absent Icon"
            />
          </label>
        </div>
      ),
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
    },
    {
      name: "Class",
      selector: (row) => row.class_info?.class_name,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="flex flex-col gap-1.5 py-1 w-full">
          <button
            onClick={() => history.push({ pathname: `/t/attendance/view/${row._id}`, state: { student: row } })}
            className="w-full justify-center inline-flex items-center px-2 py-1 bg-blue-800 hover:bg-blue-700 text-white text-[11px] font-semibold rounded-full transition-colors"
            title="View Student"
          >
            View
          </button>
          {(attendance[row._id] === 1 || attendance[row._id] === 2) && (
            <button
              onClick={() => openUnmarkModal(row._id)}
              className="w-full justify-center inline-flex items-center px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-[11px] font-semibold rounded-full border border-red-200 transition-colors whitespace-nowrap"
              title="Unmark attendance"
            >
              Unmark
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      width: "105px",
    },
  ];

  const isMorning = period === "morning";

  return (
    <div className="px-3 md:px-10 pt-4 md:pt-5 mt-24 md:mt-0">
      <BackButton history={history} />
      <div className="flex flex-wrap justify-between items-center mb-4">
        <Header category="Attendance" title="Mark Student Attendance" />
      </div>

      <div className="p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        {/* Period Selector */}
        <div className="flex justify-center mb-6">
          <div className="flex bg-gray-100 rounded-2xl p-1 gap-1">
            <button
              onClick={() => setPeriod("morning")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isMorning
                  ? "bg-amber-500 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              }`}
            >
              <MdWbSunny className={`text-lg ${isMorning ? "text-white" : "text-amber-400"}`} />
              Morning
            </button>
            <button
              onClick={() => setPeriod("afternoon")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                !isMorning
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              }`}
            >
              <MdCloud className={`text-lg ${!isMorning ? "text-white" : "text-blue-400"}`} />
              Afternoon
            </button>
          </div>
        </div>

        {/* Attendance Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-gray-800">{totalStudents}</p>
            <p className="text-xs text-gray-500 mt-1">Total</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{presentCount}</p>
            <p className="text-xs text-green-500 mt-1">Present</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{absentCount}</p>
            <p className="text-xs text-red-400 mt-1">Absent</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            <p className="text-xs text-yellow-500 mt-1">Pending</p>
          </div>
        </div>

        {/* Controls Row — single row on desktop, stacked on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <input
            type="date"
            onChange={(e) => setDate(e.target.value)}
            value={date}
            className="w-full sm:w-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-full focus:ring-blue-500 focus:border-blue-500 p-2.5"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => markAll(1)}
              className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold rounded-full text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 transition-colors whitespace-nowrap"
            >
              All Present
            </button>
            <button
              type="button"
              onClick={() => markAll(2)}
              className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold rounded-full text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors whitespace-nowrap"
            >
              All Absent
            </button>
          </div>
          <div className="relative flex-1">
            <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
              <MdSearch />
            </div>
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-full focus:ring-blue-500 focus:border-blue-500 pl-8 p-2.5"
              placeholder="Search"
            />
          </div>
          <button
            type="button"
            onClick={handleSaveClick}
            className={`w-full sm:w-auto shrink-0 py-2.5 px-5 text-sm font-medium rounded-full text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors whitespace-nowrap ${
              isMorning
                ? "bg-amber-500 hover:bg-amber-600 focus:ring-amber-400"
                : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
            }`}
            disabled={saving}
          >
            {saving ? "Saving..." : `Save ${isMorning ? "Morning" : "Afternoon"} Attendance`}
          </button>
        </div>

        {/* Bulk unmark toolbar */}
        {selectedForUnmark.size > 0 && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4">
            <p className="text-sm font-medium text-amber-800">
              {selectedForUnmark.size} student{selectedForUnmark.size > 1 ? "s" : ""} selected
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedForUnmark(new Set())}
                className="text-xs text-amber-700 hover:text-amber-900 font-medium"
              >
                Clear
              </button>
              <button
                onClick={() => openUnmarkModal()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-full transition-colors"
              >
                Unmark Selected ({selectedForUnmark.size})
              </button>
            </div>
          </div>
        )}

        {attendanceLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
            <div className="w-3 h-3 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
            Refreshing attendance...
          </div>
        )}

        <div className="border rounded-3xl">
          <DataTable
            data={filteredData}
            columns={columns}
            getRowId={(row) => row._id}
            keyField="_id"
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            conditionalRowStyles={[
              {
                when: (row) => selectedForUnmark.has(row._id),
                style: { backgroundColor: "rgb(255, 251, 235)", borderLeft: "3px solid rgb(245, 158, 11)" },
              },
            ]}
            highlightOnHover
            striped
          />
        </div>

        {/* Bottom save button — only shown when list is long */}
        {filteredData?.length > 12 && (
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleSaveClick}
              className={`py-2.5 px-8 text-sm font-semibold rounded-full text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors ${
                isMorning
                  ? "bg-amber-500 hover:bg-amber-600 focus:ring-amber-400"
                  : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
              }`}
              disabled={saving}
            >
              {saving ? "Saving..." : `Save ${isMorning ? "Morning" : "Afternoon"} Attendance`}
            </button>
          </div>
        )}
      </div>

      {/* Unmark confirmation modal */}
      {showUnmarkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Unmark Attendance</h3>
                <p className="text-sm text-gray-500">
                  {selectedForUnmark.size} student{selectedForUnmark.size > 1 ? "s" : ""} · {isMorning ? "Morning" : "Afternoon"} · {date}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              This will clear the attendance record for the selected student{selectedForUnmark.size > 1 ? "s" : ""}. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnmarkModal(false)}
                className="flex-1 py-3 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-colors"
                disabled={unmarking}
              >
                Cancel
              </button>
              <button
                onClick={confirmUnmark}
                disabled={unmarking}
                className="flex-1 py-3 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-2xl disabled:opacity-50 transition-colors"
              >
                {unmarking ? "Removing..." : "Remove Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Absent reason modal */}
      {showAbsentReasonModal && (() => {
        const absentStudents = students.filter((s) => attendance[s._id] === 2);
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-base font-bold text-gray-900">Reason for Absence <span className="text-gray-400 font-normal text-sm">(optional)</span></h3>
                  <button
                    onClick={() => saveAttendance(absentReasons)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  {absentStudents.length} student{absentStudents.length > 1 ? "s" : ""} marked absent · {isMorning ? "Morning" : "Afternoon"}
                </p>

                {/* Fill-all shortcut (shown only when multiple absent) */}
                {absentStudents.length > 1 && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={sharedReason}
                      onChange={(e) => setSharedReason(e.target.value)}
                      placeholder="Same reason for all…"
                      className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                    <button
                      type="button"
                      disabled={!sharedReason.trim()}
                      onClick={() => {
                        const updated = {};
                        absentStudents.forEach((s) => { updated[s._id] = sharedReason; });
                        setAbsentReasons(updated);
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-red-50 text-red-600 border border-red-200 rounded-xl disabled:opacity-40 hover:bg-red-100 transition-colors whitespace-nowrap"
                    >
                      Apply all
                    </button>
                  </div>
                )}
              </div>

              {/* Scrollable student list */}
              <div className="overflow-y-auto flex-1 px-5 py-3 space-y-3">
                {absentStudents.map((s) => (
                  <div key={s._id} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-red-700 font-bold text-xs uppercase">
                        {(s.firstname?.[0] || '') + (s.lastname?.[0] || '')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 leading-tight mb-1">
                        {s.lastname} {s.firstname}
                      </p>
                      <input
                        type="text"
                        value={absentReasons[s._id] || ''}
                        onChange={(e) => setAbsentReasons((prev) => ({ ...prev, [s._id]: e.target.value }))}
                        placeholder="Enter reason (optional)…"
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-colors"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAbsentReasonModal(false)}
                    className="flex-1 py-3 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveAttendance(absentReasons)}
                    disabled={saving}
                    className="flex-1 py-3 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-2xl disabled:opacity-50 transition-colors"
                    style={{ background: isMorning ? undefined : '#2563eb' }}
                  >
                    {saving ? "Saving…" : `Save ${isMorning ? "Morning" : "Afternoon"}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {error && <AlertNotification type="error" message={error} onClose={() => setError("")} />}
      {success && <AlertNotification type="success" message={success} onClose={() => setSuccess("")} />}
    </div>
  );
};

export default StudentAttendance;
